/**
 * The consumer session — the surface's cross-cutting auth state. Holds the
 * signed-in user and drives the in-memory token the api client + `/ws` re-read
 * per request/connect. A plain store of signal getters + async actions
 * (mirroring the other product stores and the console's `SessionStore`),
 * constructed ONCE in the composition root (`App`) and passed down as a prop —
 * components never touch the api client directly (DIP).
 *
 * `register`/`login`/`verifyAge` set + persist the token (writing `4f:token`,
 * which is what unlocks the authenticated surface); `logout` clears it (and
 * best-effort revokes the refresh family edge-side); `restore` re-hydrates from
 * the httpOnly refresh cookie on boot and drops any stale access token when the
 * edge has no live session. `restored` reports once that boot round-trip has
 * SETTLED (success, failure, or an epoch-race bail-out) — `AuthGate` keys on it
 * to hold a splash instead of flashing the front door at an already-signed-in
 * reload.
 *
 * `adoptRenewed`/`expire` are the two hooks the api client's `401`
 * refresh-and-retry recovery drives (its `SessionRecovery` sink): `adoptRenewed`
 * silently swaps in a session the edge renewed mid-request (no `loading()`
 * flicker); `expire` drops a session whose refresh family the edge has already
 * given up on, with no further round-trip — the rejection itself is the proof
 * there is nothing left to revoke.
 */
import { type ApiError, isErr } from '@4f/shared';
import { type Accessor, createSignal } from 'solid-js';
import type { ApiClient, LoginRequest, RegisterRequest, VerifyAgeRequest } from '../api/client.ts';
import { isTransportFailure } from '../api/errors.ts';
import type { AuthUserView, SessionView } from '../api/types.ts';
import { storeToken } from '../session/token-storage.ts';

export interface SessionStore {
  /** The signed-in user, or null when logged out. */
  readonly user: Accessor<AuthUserView | null>;
  /** True once a session token is held (the surface-unlock gate). */
  readonly authenticated: Accessor<boolean>;
  /** True once the explicit-content age gate is cleared (`user.ageOk`). */
  readonly ageOk: Accessor<boolean>;
  /** True while an auth round-trip (register/login/verify/restore) is in flight. */
  readonly loading: Accessor<boolean>;
  /** The last auth error (bad credentials, taken email, revoked), or null. */
  readonly error: Accessor<ApiError | null>;
  /** True once the boot re-hydration attempt has SETTLED — succeeded or not. */
  readonly restored: Accessor<boolean>;
  /**
   * True when the boot re-hydration could not REACH the edge — as opposed to
   * reaching it and being told the session is dead. The stored access token is
   * kept in this state (it may well still be valid), and `AuthGate` shows the
   * unreachable surface instead of a login form that cannot work.
   *
   * Cleared by the next {@link SessionStore.restore} that gets an answer.
   */
  readonly unreachable: Accessor<boolean>;
  /** Create the account + mint the first session; resolves `true` on success. */
  register(input: RegisterRequest): Promise<boolean>;
  /** Authenticate with credentials (+ optional captcha); resolves `true` on success. */
  login(input: LoginRequest): Promise<boolean>;
  /** Clear the age gate; resolves `true` once `ageOk` is granted. */
  verifyAge(input: VerifyAgeRequest): Promise<boolean>;
  /** Clear the session + stored token, and revoke the refresh family edge-side. */
  logout(): void;
  /** Re-hydrate from the httpOnly refresh cookie on boot; drops a stale token. */
  restore(): Promise<void>;
  /**
   * Adopt a session the api client renewed under a `401` (token + user). Not an
   * auth round-trip the user asked for, so it must NOT touch `loading()` — the
   * front door must not flicker mid-session — but it IS a session mutation, so it
   * advances the same epoch a concurrent `restore()` checks.
   */
  adoptRenewed(session: SessionView): void;
  /**
   * The refresh family is gone (the client's 401 recovery gave up) — clear the
   * session locally. No `logout()` round-trip: the refresh the edge just rejected
   * IS the proof there is nothing left to revoke.
   */
  expire(): void;
}

/** Injected deps — the api client + the in-memory token setter (the composition root owns both). */
export interface SessionStoreDeps {
  readonly api: ApiClient;
  /** Set the in-memory token the api client's + realtime's supplier reads. */
  readonly setToken: (token: string | null) => void;
  /** Whether a token is currently held (in memory or storage) — the `restore` cleanup guard. */
  readonly hasToken: () => boolean;
  /**
   * Drop any device-local state that belongs to the account signing OUT — today
   * the offline outbox, whose queued turns would otherwise be replayed under the
   * next account to sign in here. Explicit sign-out only, never `expire()`.
   */
  readonly onSignedOut?: () => void;
}

/** Build the session store around the injected api client + token setter. */
export function createSessionStore(deps: SessionStoreDeps): SessionStore {
  const [user, setUser] = createSignal<AuthUserView | null>(null);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal<ApiError | null>(null);
  const [restored, setRestored] = createSignal(false);
  const [unreachable, setUnreachable] = createSignal(false);
  // Monotonic session epoch, bumped on every session mutation (adopt/logout).
  // The boot `restore()` snapshots it before its refresh round-trip and bails if
  // a manual login/register/verifyAge/logout advanced it meanwhile — see there.
  let epoch = 0;

  /** Push the token to both sinks: the in-memory supplier AND `4f:token`. */
  function applyToken(token: string | null): void {
    deps.setToken(token);
    storeToken(token);
  }

  /** Persist a minted session (token + user) and clear any prior error. */
  function adopt(session: { accessToken: string; user: AuthUserView }): void {
    applyToken(session.accessToken);
    setUser(session.user);
    setError(null);
    epoch += 1;
  }

  /** Run an auth round-trip, adopting its session on success or surfacing the error. */
  async function authenticate(
    call: () => Promise<Awaited<ReturnType<ApiClient['login']>>>,
  ): Promise<boolean> {
    setLoading(true);
    setError(null);
    try {
      const result = await call();
      if (isErr(result)) {
        setError(result.error);
        return false;
      }
      adopt(result.value);
      return true;
    } finally {
      setLoading(false);
    }
  }

  function logout(): void {
    // Best-effort server-side revocation — never block the local clear on it.
    void deps.api.logout();
    applyToken(null);
    setUser(null);
    setError(null);
    epoch += 1;
    // Anything still queued was typed by the account that is leaving. Kept, it
    // would be replayed under the NEXT account to sign in on this device — one
    // user's private message posted from another's session (and, since the
    // conversation is not theirs, rejected and then dropped).
    //
    // Only on an explicit sign-out. `expire()` is a session the edge gave up on,
    // not a user walking away: those turns are still theirs and still deliverable
    // once the session is renewed.
    deps.onSignedOut?.();
  }

  async function restore(): Promise<void> {
    setLoading(true);
    // Snapshot the epoch before the refresh round-trip. `AuthGate` shows the
    // front door for this whole call (`user()` is null), so the user can submit
    // login/register/verifyAge — or log out — while `refresh()` is in flight. If
    // any of those resolved first they bumped the epoch; bail so we neither wipe
    // a just-minted token (error path) nor clobber a fresher session (success).
    const startedAtEpoch = epoch;
    try {
      const result = await deps.api.refresh();
      if (epoch !== startedAtEpoch) {
        return;
      }
      if (isErr(result)) {
        // A failure that never reached the edge proves NOTHING about the session:
        // an ingress 503 answers `text/plain`, so the envelope read fails and this
        // branch used to run for a perfectly live session — throwing away a valid
        // access token and dropping the visitor at a login form that could not
        // work either. Keep the token, say so, and let the user retry.
        if (isTransportFailure(result.error)) {
          setUnreachable(true);
          return;
        }
        // The edge answered and has no live session behind the cookie — drop any
        // stale stored access token so the surface shows the front door instead of
        // a broken authed state.
        setUnreachable(false);
        if (deps.hasToken()) {
          applyToken(null);
        }
        return;
      }
      setUnreachable(false);
      adopt(result.value);
    } finally {
      setLoading(false);
      // Settle on EVERY path — success, the no-live-session 401, and the
      // epoch-race bail-out above. `AuthGate` holds a splash until this flips;
      // never flipping it back to `false` is what keeps that splash a one-shot.
      setRestored(true);
    }
  }

  /** See {@link SessionStore.adoptRenewed}. */
  function adoptRenewed(session: SessionView): void {
    adopt(session);
  }

  /** See {@link SessionStore.expire}. */
  function expire(): void {
    applyToken(null);
    setUser(null);
    // Deliberately no `setError(...)` here (not even to clear it): the refresh
    // the edge just rejected is a session timing out, not a credential the user
    // typed wrong. Surfacing it as `error()` would render as a raw, untranslated
    // string on whatever the login form shows next — a clean front door is the
    // correct landing, not an error state.
    epoch += 1;
  }

  return {
    user,
    authenticated: () => user() !== null,
    ageOk: () => user()?.ageOk ?? false,
    loading,
    error,
    restored,
    unreachable,
    register: (input) => authenticate(() => deps.api.register(input)),
    login: (input) => authenticate(() => deps.api.login(input)),
    verifyAge: (input) => authenticate(() => deps.api.verifyAge(input)),
    logout,
    restore,
    adoptRenewed,
    expire,
  };
}
